import type { CacheStorage } from '@shkola/api';

/**
 * The offline copy of the screens in the browser: AES-GCM encrypted in localStorage. The key is
 * a non-extractable CryptoKey kept in IndexedDB — scripts can use it but nobody can read it out,
 * and clearing the site data forgets both.
 */
const DB_NAME = 'shkola-keys';
const STORE = 'keys';

function openKeys(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

let keyPromise: Promise<CryptoKey> | undefined;

function cacheKey(): Promise<CryptoKey> {
  keyPromise ??= (async () => {
    const db = await openKeys();
    const existing = await new Promise<CryptoKey | undefined>((resolve, reject) => {
      const request = db.transaction(STORE).objectStore(STORE).get('cache');
      request.onsuccess = () => resolve(request.result as CryptoKey | undefined);
      request.onerror = () => reject(request.error);
    });
    if (existing) return existing;
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(key, 'cache');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    return key;
  })();
  return keyPromise;
}

const toBase64 = (bytes: Uint8Array) => {
  let text = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(text);
};
const fromBase64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

export const encryptedWebStorage: CacheStorage = {
  async getItem(name) {
    const stored = localStorage.getItem(name);
    if (!stored) return null;
    try {
      const [iv, data] = stored.split('.') as [string, string];
      const plain = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: fromBase64(iv) },
        await cacheKey(),
        fromBase64(data),
      );
      return new TextDecoder().decode(plain);
    } catch {
      // a copy made with a forgotten key is useless: start fresh
      localStorage.removeItem(name);
      return null;
    }
  },
  async setItem(name, value) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      await cacheKey(),
      new TextEncoder().encode(value),
    );
    localStorage.setItem(name, `${toBase64(iv)}.${toBase64(new Uint8Array(data))}`);
  },
  async removeItem(name) {
    localStorage.removeItem(name);
  },
};
